/* eslint-disable max-len */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { MLQuoteNameUtil, MLLocalDateUtil } from 'ml-portals-util-js';
import metadata from './PolicySummaryInsuredInfoComponent.metadata.json5';
import messages from './PolicySummaryInsuredInfoComponent.messages';
import styles from './PolicySummaryInsuredInfoComponent.module.scss';

function PolicySummaryInsuredInfoComponent(props) {
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { model: policyDetails, isPUP , gpaMode} = props;

    const LIMIT_COV = 'Liability';
    const SELF_INSURED_RETENTION_COV = 'Self Insured Retention';

    const getPolicyCoverageType = () => {
        if (isPUP) {
            return translator(messages.umbrella);
        }

        const lobs = _.get(policyDetails, 'lobs.value', undefined);
        const lobsPolChange = _.get(policyDetails, 'lobsPolChange.value', undefined);
        if (!_.isUndefined(lobs)) {
            return lobs.personalAuto ? translator(messages.auto)
                : MLQuoteNameUtil.getCoveragePartTypeName(policyDetails.coveragePartType.value.code);
        }
        if (!_.isUndefined(lobsPolChange)) {
            return lobsPolChange.personalAuto ? translator(messages.auto)
                : MLQuoteNameUtil.getCoveragePartTypeName(policyDetails.coveragePartType.value.code);
        }
    };

    const formatName = (person) => {
        if (!_.isUndefined(person)) {
            const middleName = person.middleName || '';
            let displayName = _.capitalize(`${person.name} `) + _.capitalize(`${middleName} `) + _.capitalize(`${person.lastName}`);

            if (person.suffix) {
                const suffixTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('NameSuffix');
                const suffixName = suffixTypelist.getCode(person.suffix).name;
                displayName += ', ';
                displayName += translator({
                    id: suffixName,
                    defaultMessage: suffixName
                });
            }
            return displayName;
        }
    };

    const appendLabelAndVal = (label, val) => {
        return (
            <>
                <span className="font-weight-bold">{label}</span>
                {val}
            </>
        );
    };

    const getTermValueFromAvailableCoverages = (covName) => {
        const coverages = policyDetails.coverageLobs.personalUmbrella;
        if (coverages && coverages.primaryCoverages) {
            const foundCov = _.find(coverages.primaryCoverages.value, (cov) => cov.name === covName);
            if (foundCov) {
                return foundCov.terms[0].chosenTermValue;
            }
        }
        console.error(new Error(`Could not retrieve term value since there is no '${covName}' coverage`));
        return null;
    };

    const convertLocalDateToDate = (localDate) => {
        const parsedDate = new Date(localDate.year, localDate.month, localDate.day);
        return moment(parsedDate).format('MM/DD/YYYY');
    };

    const effectiveDate = _.get(policyDetails, 'effective_ML.value', '');
    const expirationDate = _.get(policyDetails, 'expiration_ML.value', '');
    const policyHolder = _.get(policyDetails, 'policyHolder.value', undefined);
    const addressLine1 = _.get(policyDetails, 'mailingAddress.addressLine1.value', undefined);
    const addressLine2 = _.get(policyDetails, 'mailingAddress.addressLine2.value', undefined);
    const addressLine3 = _.get(policyDetails, 'mailingAddress.addressLine3.value', undefined);
    const city = _.get(policyDetails, 'mailingAddress.city.value', '');
    const state = _.get(policyDetails, 'mailingAddress.state.value.code', '');
    const postalCode = _.get(policyDetails, 'mailingAddress.postalCode.value', '');
    const secondaryInsured = _.get(policyDetails, 'secondaryInsured_ML.value', undefined);
    const overrides = {
        policySummaryContainer : {
            visible : !gpaMode
        },
        insured: {
            content: gpaMode ? _.upperCase(translator(messages.insured)) : translator(messages.insured)
        },
        insuranceType: {
            content: appendLabelAndVal(translator(messages.insuranceType), getPolicyCoverageType())
        },
        effectiveDate: {
            content: appendLabelAndVal(translator(messages.effectiveDate), convertLocalDateToDate(effectiveDate, true))
        },
        expirationDate: {
            content: appendLabelAndVal(translator(messages.expirationDate), convertLocalDateToDate(expirationDate, true)),
            className: isPUP ? 'vertical-spacing-20' : ''
        },
        policyHolder: {
            content: formatName(policyHolder)
        },
        addressLine1: {
            visible: !_.isUndefined(addressLine1),
            content: `${addressLine1}`
        },
        addressLine2: {
            visible: !_.isUndefined(addressLine2),
            content: `${addressLine2}`
        },
        addressLine3: {
            visible: !_.isUndefined(addressLine3),
            content: `${addressLine3}`
        },
        cityStatePostalCode: {
            content: `${city}, ${state} ${postalCode}`
        },
        secondaryInsuredContainer: {
            visible: !_.isUndefined(secondaryInsured)
        },
        secondaryInsuredName: {
            content: formatName(secondaryInsured)
        },
        secondaryAddressLine1: {
            visible: !_.isUndefined(addressLine1),
            content: `${addressLine1}`
        },
        secondaryAddressLine2: {
            visible: !_.isUndefined(addressLine2),
            content: `${addressLine2}`
        },
        secondaryAddressLine3: {
            visible: !_.isUndefined(addressLine3),
            content: `${addressLine3}`
        },
        secondaryCityStatePostalCode: {
            content: `${city}, ${state} ${postalCode}`
        },
        policyLimit: {
            visible: isPUP,
            content: isPUP ? appendLabelAndVal(translator(messages.policyLimit), getTermValueFromAvailableCoverages(LIMIT_COV)) : ''
        },
        selfInsuredRetention: {
            visible: isPUP,
            content: isPUP ? appendLabelAndVal(translator(messages.selfInsuredRetention), getTermValueFromAvailableCoverages(SELF_INSURED_RETENTION_COV)) : ''
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={policyDetails}
            overrideProps={overrides}
            classNameMap={styles}
        />
    );
}

PolicySummaryInsuredInfoComponent.propTypes = {
    model: PropTypes.shape({}).isRequired,
    isPUP: PropTypes.bool,
    gpaMode:PropTypes.bool
};

PolicySummaryInsuredInfoComponent.defaultProps = {
    isPUP: false,
    gpaMode:false
};

export default PolicySummaryInsuredInfoComponent;
